"use client";

import { useState } from "react";
import { ChevronLeft, Grid, List, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { HIGHLIGHT_CONFIG, HighlightMaterials } from "./highlights";
import { HighlightCard } from "./highlight-card";

// Mock types for demo
type ViewMode = "grid" | "list";

interface HighlightSelectViewProps {
  category: string;
  items: HighlightMaterials[];
  onSelect: (item: HighlightMaterials) => void;
  onBack: () => void;
  onDelete: (item: HighlightMaterials) => Promise<void>;
  rightTopBar?: boolean;
}

function groupByDate(items: HighlightMaterials[]) {
  const now = Date.now();

  const groups: Record<string, HighlightMaterials[]> = {
    Today: [],
    "This week": [],
    Earlier: [],
  };

  items.forEach((item) => {
    const days =
      (now - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24);

    if (days < 1) groups.Today.push(item);
    else if (days < 7) groups["This week"].push(item);
    else groups.Earlier.push(item);
  });

  return Object.entries(groups).filter(([, v]) => v.length > 0);
}

export function HighlightSelectView({
  category,
  items,
  onSelect,
  onBack,
  onDelete,
  rightTopBar,
}: HighlightSelectViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const config = HIGHLIGHT_CONFIG.find((c) => c.id === category);
  if (!config) return null;
  const groupedItems = groupByDate(items);

  return (
    <div className="flex h-full w-full flex-col bg-theme-0">
      {/* Enhanced Header with gradient */}
      <div className="relative bg-theme-0 backdrop-blur-sm">
        <div className="absolute inset-0" />

        <div className={cn("relative flex items-center h-16 pr-6", !rightTopBar ? 'justify-between' : 'justify-end')}>
          <div
            className={cn(
              "flex items-center gap-1 h-16",
              rightTopBar && "justify-end mr-4"
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="gap-1 text-sm hover:underline text-theme-0"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to chat
            </Button>

            <Separator
              orientation="vertical"
              className="mr-3 data-[orientation=vertical]:h-5"
            />

            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-lg",
                  config.color.bg,
                  config.color.border
                )}
              >
                <config.icon className={cn("h-5 w-5", config.color.text)} />
              </div>
              <div>
                <div className="text-sm font-bold text-theme-0">
                  {config.label}
                </div>
                <div className="text-xs text-theme-1 font-medium">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced View toggle */}
          <div className="flex items-center gap-1 p-1 bg-theme-2 rounded-lg">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("grid")}
              className={cn(
                "h-8 px-3 transition-all rounded-md",
                viewMode === "grid"
                  ? "bg-theme-0 shadow-theme text-theme-0"
                  : "text-theme-1 bg-theme-2 hover:text-theme-0 hover:bg-theme-1"
              )}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("list")}
              className={cn(
                "h-8 px-3 transition-all rounded-md",
                viewMode === "list"
                  ? "bg-theme-0 shadow-theme text-theme-0"
                  : "text-theme-1 bg-theme-2 hover:text-theme-0 hover:bg-theme-1"
              )}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Separator />

      {/* Content with better spacing */}
      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-8">
          {/* Hero section */}
          <div className="mb-10">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-theme-0 mb-2">
                  Your {config.labelLong}
                </h2>
                <p className="text-sm text-theme-1 max-w-2xl leading-relaxed">
                  All your saved {config.labelLong.toLowerCase()} generated from
                  your documents and notes.
                </p>
              </div>
            </div>
          </div>

          {/* Content sections */}
          {items.length > 0 ? (
            <div className="space-y-10">
              {groupedItems.map(([label, group]) => (
                <section key={label}>
                  {/* Enhanced section header */}
                  <div className="mb-5 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          config.color.bg
                        )}
                      />
                      <h3 className="text-sm font-bold text-theme-0 uppercase tracking-wider">
                        {label}
                      </h3>
                      <div className="h-5 w-5 flex items-center justify-center bg-theme-2 rounded-full">
                        <span className="text-xs font-semibold text-theme-1">
                          {group.length}
                        </span>
                      </div>
                    </div>
                    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" />
                  </div>

                  {/* Cards grid/list */}
                  <div
                    className={cn(
                      viewMode === "grid"
                        ? "grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]"
                        : "flex flex-col gap-3"
                    )}
                  >
                    {group.map((item) => (
                      <HighlightCard
                        key={item.id}
                        item={item}
                        config={config}
                        viewMode={viewMode}
                        onSelect={onSelect}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            // Enhanced empty state
            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-theme bg-theme-0 py-20 px-8">
              <div className="relative">
                <div
                  className={cn(
                    "absolute inset-0 blur-2xl opacity-30",
                    config.color.text === "text-blue-700" && "bg-blue-500",
                    config.color.text === "text-orange-700" && "bg-orange-500",
                    config.color.text === "text-emerald-800" && "bg-emerald-500"
                  )}
                />
                <div
                  className={cn(
                    "relative mb-6 flex h-20 w-20 items-center justify-center rounded-2xl shadow-theme border-2",
                    config.color.text === "text-blue-700" &&
                      "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200",
                    config.color.text === "text-orange-700" &&
                      "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200",
                    config.color.text === "text-emerald-800" &&
                      "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
                  )}
                >
                  <config.icon className={cn("h-10 w-10", config.color.text)} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-theme-0 mb-2">
                No {config.labelLong.toLowerCase()} yet
              </h3>
              <p className="text-sm text-theme-1 text-center max-w-sm">
                Your saved {config.labelLong.toLowerCase()} will appear here.
                Start generating content from your documents to see them here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
